import '@atlaskit/css-reset';
import React, { useCallback, useState } from 'react';
import { FlagsProvider } from '@atlaskit/flag';
import UFOSegment from '@atlaskit/react-ufo/segment';
import Suspense from '@atlaskit/react-ufo/suspense';
import HeartbeatDisplay from '@atlassian/heartbeat';
import { PreloadedQuery, RelayEnvironmentProvider } from 'react-relay';
import { AkAnalyticsListeners } from '@townsquare/analytics/ak-listener';
import { AnalyticsClientImpl } from '@townsquare/analytics/client';
import { AnalyticsContext } from '@townsquare/analytics/context';
import { useAtlasKitAnalytics } from '@townsquare/analytics/use-ak-analytics';
import { AppContext } from '@townsquare/app-context';
import { useLoadAppsFromResource } from '@townsquare/apps-store/hooks';
import { AuthCheck } from '@townsquare/auth';
import { StorageAuthCheck } from '@townsquare/auth/storage-auth-check';
import { returnAfterLogin } from '@townsquare/auth-utils';
import { useBrowserStorageControls } from '@townsquare/auth-utils/use-browser-storage-controls';
import { useRovoWorkspace } from '@townsquare/collab-context';
import { CrossFlowProvider } from '@townsquare/crossflow/provider';
import { CrossFlowWAC } from '@townsquare/crossflow/wac';
import { Drawers } from '@townsquare/drawers';
import { Flags } from '@townsquare/flags/wrapper';
import { HelpContextProvider } from '@townsquare/help-context';
import { UserLocaleOverrideHandler } from '@townsquare/i18n/override';
import TownsquareIntlProvider from '@townsquare/i18n/provider';
import { Modals } from '@townsquare/modals/ui';
import { RouteListener } from '@townsquare/performance/route-listener';
import { PostOfficeContextProvider } from '@townsquare/post-office/post-office-provider';
import { TownsquarePreviewPanelsProvider } from '@townsquare/preview-panels/townsquarePreviewPanelsProvider';
import { initialiseAggRelayEnvironment } from '@townsquare/relay-utils/agg-environment';
import { getRelayEnvironment } from '@townsquare/relay-utils/townsquare-environment';
import { RouteBlocker } from '@townsquare/route-blocker';
import { CapabilityRouter } from '@townsquare/router/capability-router';
import { createBrowserHistory } from '@townsquare/router/createBrowserHistory';
import { isEmbedRoute, matchRoute } from '@townsquare/router/primitives/match-route';
import { useRouteListeners } from '@townsquare/router/use-route-listeners';
import { useQueryParam } from '@townsquare/router/useQueryParam';
import { RovoChatSubscribers } from '@townsquare/rovo-chat/subscribers';
import { createSearchParamsFromCurrentUrl } from '@townsquare/search-params';
import { FullScreenSpaLoader } from '@townsquare/spa-loader';
import { fg } from '@townsquare/stat-sig/gate';
import { GlobalTheming } from '@townsquare/theme/global-theming';
import { AnalyticAttributeHandler } from './AnalyticsAttributeHandler';
import { RovoAppificationHandler } from './RovoAppificationHandler';
import { Bootstrap, BootstrapResult } from './bootstrap/Bootstrap';
import { BootstrapQuery } from './bootstrap/__generated__/BootstrapQuery.graphql';
import { useKeyboardShortcuts } from './navigation/use-keyboard-shortcuts';
import { EmbedRoute } from './router/EmbedRoute';
import { GriffinRoute } from './router/GriffinRoute';
import { useHomeRoutes, useRoute } from './router/hooks';
import { routes } from './router/routes';
const analyticsClient = new AnalyticsClientImpl('web', 'townsquare');
const history = createBrowserHistory();
const EnforceCloudId = (props: {
  cloudId?: string;
}) => {
  const route = useRoute();
  const rovoWorkspaceCloudId = useRovoWorkspace();
  const cloudId = props.cloudId ?? rovoWorkspaceCloudId;
  const [qsCloudId, setQsCloudId] = useQueryParam('cloudId');
  if ('routeType' in route && route.routeType === 'global' && qsCloudId !== cloudId) {
    setQsCloudId(cloudId ?? undefined, 'replace');
  }
  return null;
};
const InsideBootstrap = (data: BootstrapResult) => {
  // Compliance control
  useBrowserStorageControls();
  const homeRoutes = useHomeRoutes();
  const akAnalytics = useAtlasKitAnalytics();
  const isUGSHeadersMigrationEnabled = fg('townsquare_ugs_headers_migration');
  const isReact18LoopFixEnabled = fg('townsquare_react_18_loop_fix');
  const RouteWrapper = isReact18LoopFixEnabled ? Suspense : React.Fragment;
  if (isUGSHeadersMigrationEnabled) {
    initialiseAggRelayEnvironment(data.collabContext?.rovoWorkspace ?? undefined);
  }
  useKeyboardShortcuts();
  useLoadAppsFromResource();
  const [themeOverride] = useQueryParam('themeOverride');
  const {
    embed
  } = useRoute();
  return <RelayEnvironmentProvider environment={getRelayEnvironment(data.workspace?.cloudId ?? undefined)}>
			<CrossFlowProvider>
				<AkAnalyticsListeners client={akAnalytics}>
					<EnforceCloudId cloudId={data.workspace?.cloudId} />
					<AnalyticAttributeHandler />
					<RovoAppificationHandler />
					<UserLocaleOverrideHandler />
					<RovoChatSubscribers />
					{/* Restrict theme overrides to embeds only */}
					<GlobalTheming colorModeOverride={embed ? themeOverride : undefined} />
					<HeartbeatDisplay onAuthenticationFailed={returnAfterLogin} />
					<CapabilityRouter history={history} resourceContext={data} data={data.relayKey} routes={homeRoutes}>
						<TownsquarePreviewPanelsProvider featureGate="townsquare_enable_home_preview_panels">
							<RouteWrapper interactionName="home-route-switcher" fallback={<FullScreenSpaLoader />}>
								<RouteListener />
								<RouteSwitcher />
							</RouteWrapper>
						</TownsquarePreviewPanelsProvider>
					</CapabilityRouter>
					<Modals />
					<Drawers />
					<Flags />
					<RouteBlocker />
				</AkAnalyticsListeners>
			</CrossFlowProvider>
		</RelayEnvironmentProvider>;
};
export function App(): React.JSX.Element {
  const urlSearch = Object.fromEntries(createSearchParamsFromCurrentUrl());
  const matchedRoute = matchRoute(routes, window.location.pathname, urlSearch);
  const AuthenticationCheck = isEmbedRoute(matchedRoute?.route) ? StorageAuthCheck : AuthCheck;
  const [preloadedQueryRef, setPreloadedQueryRef] = useState<PreloadedQuery<BootstrapQuery> | null>(null);
  const onAuthDone = useCallback(() => {
    import('./bootstrap/preload').then(({
      preloadedQueryRef
    }) => {
      setPreloadedQueryRef(preloadedQueryRef);
    });
  }, []);
  return <UFOSegment name="atlassian-home-root">
			<AuthenticationCheck onAuthDone={onAuthDone}>
				<AppContext.Provider value={{
        app: 'home'
      }}>
					<TownsquareIntlProvider>
						<RelayEnvironmentProvider environment={getRelayEnvironment()}>
							<AnalyticsContext.Provider value={analyticsClient}>
								<PostOfficeContextProvider>
									<HelpContextProvider>
										<FlagsProvider>
											{preloadedQueryRef && <Bootstrap queryRef={preloadedQueryRef} history={history}>
													{data => <InsideBootstrap {...data} />}
												</Bootstrap>}
										</FlagsProvider>
									</HelpContextProvider>
								</PostOfficeContextProvider>
								{/* WAC via Start Cross Flow touch point support */}
								<CrossFlowWAC />
							</AnalyticsContext.Provider>
						</RelayEnvironmentProvider>
					</TownsquareIntlProvider>
				</AppContext.Provider>
			</AuthenticationCheck>
		</UFOSegment>;
}
function RouteSwitcher() {
  const {
    embed
  } = useRoute();
  useRouteListeners(); // Required to init analytics
  return embed ? <EmbedRoute /> : <GriffinRoute />;
}
export default App;